/**
 * The versioning rule — PRD 02 §6, ENGINEERING §3.
 *
 * `version` tracks IDENTITY, and only identity. Bumping re-points the base reference at a
 * fresh, unapproved instance, so it must happen when (and only when) what the primitive
 * *is* has changed.
 *
 * Axis edits never bump. Locking the fox and later adding a "leaping" pose must not
 * un-approve the fox — the base reference is the neutral variant, and axes are the
 * dimensions you vary away from it.
 */
import { canonicalJson } from './hash.js';
import { assertAxesWithinBounds } from './matrix.js';
/** The fields that ARE the primitive's identity. A change to any of them bumps `version`. */
export const IDENTITY_FIELDS = [
    'identityNotes',
    'referenceDrawingPath',
    'colourAnchor',
    'establishment',
    // A beat is baked for a specific pair, so who is in it IS what it is (PRD 02 §11.3).
    'cast',
];
/** Absent and explicitly-undefined are the same "no value" for identity comparison. */
function sameValue(a, b) {
    return canonicalJson(a) === canonicalJson(b);
}
/** True when a patch would change any identity field (deep-equal, so `Localized` compares by value). */
export function patchTouchesIdentity(primitive, patch) {
    return IDENTITY_FIELDS.some((field) => field in patch && !sameValue(primitive[field], patch[field]));
}
/**
 * Apply a patch, returning a NEW primitive. Pure: the input is never mutated.
 *
 * `version` increments iff an identity field actually changed, or `relock` is set. Axis
 * edits, renames, and description changes leave the version — and therefore the base
 * reference and its approval — untouched.
 *
 * A patch that touches `axes` is checked against the matrix guardrail and refused with
 * `AxesTooLargeError` if it would describe more variants than the ceiling allows.
 */
export function applyPrimitivePatch(primitive, patch, options = {}) {
    // Axis edits are free of the version rule, but not of the guardrail: axes multiply, and
    // the ceiling is enforced where axes actually change rather than at render time.
    if (patch.axes !== undefined)
        assertAxesWithinBounds(patch.axes);
    const bump = options.relock === true || patchTouchesIdentity(primitive, patch);
    const next = { ...primitive, ...patch };
    next.version = primitive.version + (bump ? 1 : 0);
    return next;
}
//# sourceMappingURL=versioning.js.map